import { DEFAULT_COMPETITIVE_CONFIG } from "../game/config";
import type {
  MatchDebugSnapshot,
  MatchSnapshot,
  SnakeState,
} from "../game/types";
import type { Vec2 } from "../game/math";
import type { CosmeticId, ThemePreference } from "../progress/schema";
import {
  CANVAS_PALETTES,
  COSMETIC_VISUAL_IDS,
  getCosmeticStyle,
  type CanvasPalette,
  type CosmeticVisualId,
} from "../theme/theme";
import { cameraScaleForLength, cameraSmoothingFactor } from "./interpolation";
import { buildCosmeticGeometry } from "./cosmetic-geometry";
import {
  cosmeticBodyLayers,
  cosmeticHeadShapes,
  cosmeticMarkShapes,
  paintCosmeticShapes,
} from "./cosmetic-art";

export interface RenderOptions {
  readonly theme: ThemePreference;
  readonly performance: "auto" | "quality" | "low";
  readonly reducedMotion: boolean;
  readonly reducedParticles: boolean;
  readonly debug: boolean;
  readonly equippedCosmetic: CosmeticId;
  readonly renderDeltaSeconds: number;
}

type ResizeOptions = Pick<RenderOptions, "performance">;

const hash = (value: string): number => {
  let result = 0;
  for (const character of value)
    result = (result * 31 + character.charCodeAt(0)) >>> 0;
  return result;
};

const BOT_COSMETICS = COSMETIC_VISUAL_IDS.filter((id) => id !== "gloam");

const drawPath = (
  context: CanvasRenderingContext2D,
  points: readonly Vec2[],
): void => {
  const first = points[0];
  if (!first) return;
  context.beginPath();
  context.moveTo(first.x, first.y);
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (point) context.lineTo(point.x, point.y);
  }
};

export const botCosmetic = (snakeId: string): CosmeticVisualId => {
  return BOT_COSMETICS[hash(snakeId) % BOT_COSMETICS.length] ?? "lime";
};

export class CanvasRenderer {
  private context: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private scale = 1;
  private camera = { x: 0, y: 0 };
  private atmosphere: CanvasGradient | null = null;
  private atmosphereTheme: ThemePreference | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("canvas-2d-unavailable");
    this.context = context;
  }

  resize(options: ResizeOptions): void {
    const bounds = this.canvas.getBoundingClientRect();
    const automaticLow =
      options.performance === "auto" && window.devicePixelRatio > 2;
    const pixelRatio =
      options.performance === "low" || automaticLow
        ? 1
        : Math.min(window.devicePixelRatio, 2);
    this.atmosphere = null;
    this.width = Math.max(1, bounds.width);
    this.height = Math.max(1, bounds.height);
    this.canvas.width = Math.round(this.width * pixelRatio);
    this.canvas.height = Math.round(this.height * pixelRatio);
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  }

  render(
    snapshot: MatchSnapshot,
    debug: MatchDebugSnapshot,
    options: RenderOptions,
  ): void {
    const context = this.context;
    context.save();
    context.setTransform(
      this.canvas.width / this.width,
      0,
      0,
      this.canvas.height / this.height,
      0,
      0,
    );
    const palette = CANVAS_PALETTES[options.theme];
    this.drawBackground(options, palette);

    const player = snapshot.snakes.find(
      (snake) => snake.controller === "human",
    );
    const targetCamera = player?.position ?? this.camera;
    const smoothing = cameraSmoothingFactor(
      options.renderDeltaSeconds,
      options.reducedMotion,
    );
    this.camera.x += (targetCamera.x - this.camera.x) * smoothing;
    this.camera.y += (targetCamera.y - this.camera.y) * smoothing;
    const playerLength = player?.length ?? 120;
    const targetScale = cameraScaleForLength(playerLength);
    this.scale += (targetScale - this.scale) * smoothing;

    context.translate(this.width / 2, this.height / 2);
    context.scale(this.scale, this.scale);
    context.translate(-this.camera.x, -this.camera.y);

    this.drawGrid(palette, options);
    this.drawArena(palette);
    this.drawTide(debug, options, palette);
    this.drawFoods(snapshot, options, palette);
    for (const snake of snapshot.snakes)
      this.drawSnake(snake, options, palette);
    if (options.debug) this.drawDebug(debug, palette);
    context.restore();
  }

  private drawBackground(options: RenderOptions, palette: CanvasPalette): void {
    if (options.performance === "low" || options.reducedParticles) {
      this.context.fillStyle = palette.background;
      this.context.fillRect(0, 0, this.width, this.height);
      return;
    }
    if (!this.atmosphere || this.atmosphereTheme !== options.theme) {
      this.atmosphere = this.context.createRadialGradient(
        this.width * 0.5,
        this.height * 0.42,
        0,
        this.width * 0.5,
        this.height * 0.42,
        Math.max(this.width, this.height) * 0.72,
      );
      this.atmosphere.addColorStop(0, palette.ambient);
      this.atmosphere.addColorStop(1, palette.background);
      this.atmosphereTheme = options.theme;
    }
    this.context.fillStyle = this.atmosphere;
    this.context.fillRect(0, 0, this.width, this.height);
  }

  private drawGrid(palette: CanvasPalette, options: RenderOptions): void {
    const context = this.context;
    const radius = DEFAULT_COMPETITIVE_CONFIG.arenaRadius;
    const left = Math.max(
      -radius,
      this.camera.x - this.width / (2 * this.scale),
    );
    const right = Math.min(
      radius,
      this.camera.x + this.width / (2 * this.scale),
    );
    const top = Math.max(
      -radius,
      this.camera.y - this.height / (2 * this.scale),
    );
    const bottom = Math.min(
      radius,
      this.camera.y + this.height / (2 * this.scale),
    );
    context.save();
    context.strokeStyle = palette.grid;
    context.lineWidth = 1 / this.scale;
    context.beginPath();
    for (let x = Math.ceil(left / 100) * 100; x <= right; x += 100) {
      context.moveTo(x, top);
      context.lineTo(x, bottom);
    }
    for (let y = Math.ceil(top / 100) * 100; y <= bottom; y += 100) {
      context.moveTo(left, y);
      context.lineTo(right, y);
    }
    context.stroke();
    if (options.performance !== "low") {
      context.strokeStyle = palette.gridMarker;
      context.beginPath();
      const tick = 2.2 / this.scale;
      for (let x = Math.ceil(left / 200) * 200; x <= right; x += 200) {
        for (let y = Math.ceil(top / 200) * 200; y <= bottom; y += 200) {
          context.moveTo(x - tick, y);
          context.lineTo(x + tick, y);
          context.moveTo(x, y - tick);
          context.lineTo(x, y + tick);
        }
      }
      context.stroke();
    }
    context.restore();
  }

  private drawArena(palette: CanvasPalette): void {
    const context = this.context;
    const radius = DEFAULT_COMPETITIVE_CONFIG.arenaRadius;
    context.save();
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.strokeStyle = palette.arena;
    context.lineWidth = 5;
    context.setLineDash([4, 10]);
    context.stroke();
    context.beginPath();
    context.arc(0, 0, radius + 22, 0, Math.PI * 2);
    context.strokeStyle = palette.arenaGlow;
    context.lineWidth = 18;
    context.setLineDash([]);
    context.stroke();
    context.restore();
  }

  private drawTide(
    debug: MatchDebugSnapshot,
    options: RenderOptions,
    palette: CanvasPalette,
  ): void {
    const radius = debug.tide.radius;
    if (radius <= 0) return;
    const context = this.context;
    context.save();
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.strokeStyle = palette.tide;
    context.lineWidth = options.reducedMotion ? 2 : 4;
    context.setLineDash([3, 12]);
    context.stroke();
    context.restore();
  }

  private drawFoods(
    snapshot: MatchSnapshot,
    options: RenderOptions,
    palette: CanvasPalette,
  ): void {
    const context = this.context;
    const halfWidth = this.width / (2 * this.scale) + 18;
    const halfHeight = this.height / (2 * this.scale) + 18;
    const detail = options.performance !== "low" && !options.reducedParticles;
    context.save();
    for (const food of snapshot.foods) {
      if (
        Math.abs(food.position.x - this.camera.x) > halfWidth ||
        Math.abs(food.position.y - this.camera.y) > halfHeight
      )
        continue;
      const resonant = food.resonantUntil > snapshot.elapsedSeconds;
      const radius = food.radius;
      const { x, y } = food.position;
      const color =
        food.source === "elimination"
          ? palette.food.elimination
          : food.source === "boost"
            ? palette.food.boost
            : resonant
              ? palette.food.resonant
              : palette.food.natural;
      if (detail) {
        context.beginPath();
        context.arc(x, y, radius * 2.3, 0, Math.PI * 2);
        context.fillStyle = color;
        context.globalAlpha = 0.1;
        context.fill();
        context.globalAlpha = 1;
      }
      context.beginPath();
      if (food.source === "elimination") {
        context.moveTo(x, y - radius);
        context.lineTo(x + radius * 0.86, y);
        context.lineTo(x, y + radius);
        context.lineTo(x - radius * 0.86, y);
        context.closePath();
      } else if (food.source === "boost") {
        context.moveTo(x, y - radius);
        context.lineTo(x + radius * 0.86, y + radius * 0.5);
        context.lineTo(x - radius * 0.86, y + radius * 0.5);
        context.closePath();
      } else context.arc(x, y, radius * 0.83, 0, Math.PI * 2);
      context.fillStyle = color;
      context.fill();
      if (detail) {
        context.beginPath();
        context.arc(
          x - radius * 0.2,
          y - radius * 0.23,
          radius * 0.23,
          0,
          Math.PI * 2,
        );
        context.fillStyle = palette.food.core;
        context.fill();
      }
      if (resonant) {
        context.beginPath();
        context.arc(x, y, radius + 3.5, 0, Math.PI * 2);
        context.strokeStyle = palette.food.resonant;
        context.lineWidth = 0.85;
        context.stroke();
      }
    }
    context.restore();
  }

  private drawSnake(
    snake: SnakeState,
    options: RenderOptions,
    palette: CanvasPalette,
  ): void {
    if (!snake.alive || snake.trail.points.length === 0) return;
    const context = this.context;
    const player = snake.controller === "human";
    const cosmetic = player ? options.equippedCosmetic : botCosmetic(snake.id);
    const style = getCosmeticStyle(options.theme, cosmetic);
    const detailLevel =
      options.performance === "low" || options.reducedParticles
        ? "reduced"
        : "quality";
    const geometry = buildCosmeticGeometry(
      snake.trail.points,
      snake.width,
      style.pattern,
      detailLevel,
      { center: snake.position, angle: snake.heading },
    );

    context.save();
    context.lineCap = "round";
    context.lineJoin = "round";
    context.setLineDash([]);
    const layers = cosmeticBodyLayers(style, detailLevel);
    for (const [index, layer] of layers.entries()) {
      context.save();
      // Only boosting adds a small halo, once; detail passes never multiply blur.
      context.shadowColor = style.accent;
      context.shadowBlur =
        index === 0 && detailLevel === "quality" && snake.boosting
          ? Math.min(5, style.glowBlur + 2)
          : 0;
      context.translate(
        layer.offset.x * snake.width,
        layer.offset.y * snake.width,
      );
      context.globalAlpha = layer.opacity;
      context.strokeStyle = layer.color;
      context.lineWidth = snake.width * layer.width;
      drawPath(context, snake.trail.points);
      context.stroke();
      context.restore();
    }
    context.shadowBlur = 0;
    if (geometry.surfaceDetail === "tide-ripple") {
      for (const sample of geometry.samples) {
        if (sample.index % 2 !== 0) continue;
        context.save();
        context.translate(sample.center.x, sample.center.y);
        context.rotate(sample.angle);
        context.scale(sample.radius, sample.radius);
        context.beginPath();
        context.moveTo(-0.38, -0.65);
        context.quadraticCurveTo(0.25, 0, -0.38, 0.65);
        context.strokeStyle = style.facet;
        context.lineWidth = 0.09;
        context.globalAlpha = 0.62;
        context.stroke();
        context.restore();
      }
    }
    if (
      style.pattern === "signal" ||
      style.pattern === "tide" ||
      style.pattern === "circuit"
    ) {
      context.setLineDash(style.dash.map((value) => value * snake.width));
      // Use the complete centerline so decorative spines cannot cut across tight turns.
      drawPath(context, snake.trail.points);
      context.strokeStyle = style.motifOutline;
      context.lineWidth = snake.width * 0.12;
      context.globalAlpha = 0.7;
      context.stroke();
      context.strokeStyle = style.accent;
      context.lineWidth = snake.width * 0.045;
      context.globalAlpha = 0.9;
      context.stroke();
      context.globalAlpha = 1;
      context.setLineDash([]);
    }
    for (const mark of geometry.marks) {
      context.save();
      context.translate(mark.center.x, mark.center.y);
      context.rotate(mark.angle);
      context.scale(mark.radius, mark.radius);
      paintCosmeticShapes(context, cosmeticMarkShapes(mark, style));
      context.restore();
    }
    if (!player) {
      context.setLineDash([snake.width * 1.4, snake.width * 0.45]);
      context.strokeStyle = style.motifOutline;
      context.lineWidth = Math.max(1.2, snake.width * 0.1);
      drawPath(context, snake.trail.points);
      context.stroke();
      context.setLineDash([]);
    }
    context.translate(geometry.head.center.x, geometry.head.center.y);
    context.rotate(geometry.head.angle);
    context.save();
    context.scale(geometry.head.radius, geometry.head.radius);
    paintCosmeticShapes(context, cosmeticHeadShapes(style));
    context.restore();
    if ((snake.protectionRemainingSeconds ?? 0) > 0) {
      const remaining = snake.protectionRemainingSeconds ?? 0;
      context.beginPath();
      context.arc(0, 0, snake.width * 1.45, 0, Math.PI * 2);
      context.strokeStyle = palette.protection;
      context.lineWidth = Math.max(2, snake.width * 0.13);
      context.setLineDash(
        remaining <= 1 ? [snake.width * 0.22, snake.width * 0.36] : [],
      );
      context.stroke();
      context.setLineDash([]);
    }
    context.restore();
  }

  private drawDebug(debug: MatchDebugSnapshot, palette: CanvasPalette): void {
    const context = this.context;
    context.save();
    context.lineWidth = 1;
    context.strokeStyle = palette.debug;
    for (const segment of debug.bodySegments) {
      context.beginPath();
      context.moveTo(segment.start.x, segment.start.y);
      context.lineTo(segment.end.x, segment.end.y);
      context.lineWidth = segment.radius * 2;
      context.globalAlpha = 0.16;
      context.stroke();
    }
    context.globalAlpha = 1;
    context.lineWidth = 1;
    for (const snake of debug.snakes) {
      context.beginPath();
      context.arc(snake.head.x, snake.head.y, snake.headRadius, 0, Math.PI * 2);
      context.stroke();
      for (const sample of snake.bodySamples) {
        context.fillStyle = palette.debug;
        context.fillRect(sample.x - 1, sample.y - 1, 2, 2);
      }
    }
    if (debug.spawn) {
      const {
        candidate,
        nearestDangerousBody,
        nearestDangerousHead,
        required,
      } = debug.spawn;
      context.setLineDash([8, 8]);
      context.globalAlpha = 0.72;
      for (const radius of [required.bodyDistance, required.headDistance]) {
        context.beginPath();
        context.arc(candidate.x, candidate.y, radius, 0, Math.PI * 2);
        context.stroke();
      }
      context.setLineDash([]);
      for (const danger of [nearestDangerousHead, nearestDangerousBody]) {
        if (!danger) continue;
        context.beginPath();
        context.moveTo(candidate.x, candidate.y);
        context.lineTo(danger.point.x, danger.point.y);
        context.stroke();
      }
    }
    context.restore();
  }
}
